import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  EditableSlotGroup,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

// ============================================================================
// Slot group definitions
// ============================================================================
// Mirrors the Header's schedule_tour_link so this CTA can be edited with the
// same field shape (URL / label / target) and defaults visitors already know
// from the top nav. Kept as a module-level literal so the schema extractor
// picks it up statically.
const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ThankYouContent() {
  useRevealStyles();
  useComfyCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const scheduleTourLink = useSlotGroup('thankyoucontent.schedule_tour_link', scheduleTourLinkDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
        />

        {/* CTA — mirrors the Header's Schedule a Tour pill via the shared
            .comfy-cta family (secondary fill → surface hover with auto-
            contrast text). */}
        <div className="mt-8 md:mt-10">
          <EditableSlotGroup
            groupPath="thankyoucontent.schedule_tour_link"
            definition={scheduleTourLinkDef}
          >
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="comfy-cta thankyou-schedule-btn"
            >
              {scheduleTourLink.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .thankyou-schedule-btn {
          padding: 1.1rem 1.75rem;
          white-space: nowrap;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(ThankYouContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ThankYouContent;
